/**
 * studentSettingsAdapter — le compte connecté → les quatre pages de « Paramètres », et le retour :
 * ce que chaque page enregistre → le corps de `authService.updateStudentAccount`.
 *
 * Port de `student/account/SettingsTabNavigator.js` (les valeurs par défaut du formulaire, son schéma
 * et `onSubmit`/`dataStudent`) et de ses onglets : `SettingsGeneralScreen` (la ligne d'abonnement de
 * `SubscriptionStateCard`), `SettingsAddressScreen` (`AddressFormFields`), `SettingsProfileScreen`,
 * `SettingsConfigurationScreen` (+ `NotificationSettingsItem`).
 *
 * L'ancien écran n'avait qu'UN formulaire pour les quatre onglets, envoyé en entier à chaque
 * enregistrement. Chaque page enregistre seule ici, et envoie quand même le corps entier, les autres
 * pages avec les valeurs du compte : c'est le corps de l'ancien formulaire. Le serveur ne touche pas
 * à un champ absent, mais il écrit tout champ envoyé, même vide.
 */
import moment from "moment"
import { postcodeValidator, postcodeValidatorExistsForCountry } from "postcode-validator"

import {
    countryOptions,
    qualities as qualityOptions,
    sportOptions,
    studentClasses,
    subjectPriorities,
} from "../../../../../data/constants/formConstants"
import yup, { phoneRegExp } from "../../../../../data/utility/validation"
import {
    type UINotificationSetting,
    type UISelectOption,
    type UIStudentAddressSettings,
    type UIStudentGeneralSettings,
    type UIStudentProfileCatalogs,
    type UIStudentProfileSettings,
    type UIStudentSubscriptionSummary,
} from "../../../ui/types/studentAccount"
import { formatDayMonthYear } from "../../../ui/formatters/dateFormatter"
import { SUBSCRIBED_STATUSES, SUBSCRIPTION_PAUSED_STATUSES } from "../../parent/Subscription/subscriptionAdapter"
import { laravelFieldErrors } from "../shared/laravelFieldErrors"
import { canReadSubject } from "../shared/studentOfferAdapter"
import { hasRole } from "./studentAccountAdapter"

type ApiSubject = { id?: number | string; name?: string }

/** Un canal : `true`/`false` réglé, absent jamais touché. */
type ApiNotificationChannels = { push?: boolean; email?: boolean }

/** `user.notification_settings` : par type, ses canaux et son heure de rappel. */
export type ApiNotificationSettings = Record<string, { channels?: ApiNotificationChannels; custom_time?: string }>

/** Un type de notification des données statiques (`BaseNotification::$list`). */
export type ApiNotificationType = {
    slug?: string
    name?: string
    roles?: string[]
    config?: { custom_time?: boolean; channels?: ApiNotificationChannels }
}

export type ApiPersonalWorkForm = {
    frequency?: { values?: string[] }
    duration?: { values?: string[] }
}

export type ApiStudentSettingsUser = {
    roles?: string[]
    firstname?: string | null
    lastname?: string | null
    email?: string | null
    mobile?: string | null
    parent_firstname?: string | null
    parent_lastname?: string | null
    parent_email?: string | null
    parent_mobile?: string | null
    reason?: string | null
    address?: {
        street?: string | null
        complement?: string | null
        postal_code?: string | null
        city?: string | null
        country?: string | null
    } | null
    profile?: {
        class?: string | null
        school_name?: string | null
        interests?: string | null
        subject_priority?: string | null
        qualities?: string[] | null
        future_job?: string | null
        future_orientation?: string | null
        personal_work?: { frequency?: string | null; duration?: string | null } | null
    } | null
    specialty_school_subjects?: ApiSubject[] | null
    prioritized_school_subjects?: ApiSubject[] | null
    appreciated_school_subjects?: ApiSubject[] | null
    notification_settings?: ApiNotificationSettings | null
    subscription_state?: { plan?: string | null; status?: string | null } | null
    trial_ends_at?: string | null
}

// ── Les valeurs de chaque page ──────────────────────────────────────────────────────────────────

export function toGeneralSettings(user: ApiStudentSettingsUser): UIStudentGeneralSettings {
    return {
        firstname: user.firstname ?? "",
        lastname: user.lastname ?? "",
        mobile: user.mobile ?? "",
        email: user.email ?? "",
        parentFirstname: user.parent_firstname ?? "",
        parentLastname: user.parent_lastname ?? "",
        parentMobile: user.parent_mobile ?? "",
        parentEmail: user.parent_email ?? "",
    }
}

/** Le pays du compte s'il est dans la liste, la France sinon — le repli de l'ancien formulaire. */
export function toAddressSettings(user: ApiStudentSettingsUser): UIStudentAddressSettings {
    const country = user.address?.country
    const known = (countryOptions as Array<{ value: string }>).some((option) => option.value === country)
    return {
        address: user.address?.street ?? "",
        addressComplement: user.address?.complement ?? "",
        postalCode: user.address?.postal_code ?? "",
        city: user.address?.city ?? "",
        countryId: country && known ? country : "FR",
    }
}

const subjectIds = (subjects: ApiSubject[] | null | undefined): string[] =>
    (subjects ?? []).filter((subject) => subject?.id != null).map((subject) => String(subject.id))

export function toProfileSettings(user: ApiStudentSettingsUser): UIStudentProfileSettings {
    const profile = user.profile ?? {}
    const priority = profile.subject_priority
    const knownPriority = (subjectPriorities as Array<{ value: string }>).some((option) => option.value === priority)
    return {
        classId: profile.class ?? "",
        sportId: profile.interests ?? "",
        specialtySubjectIds: subjectIds(user.specialty_school_subjects),
        schoolName: profile.school_name ?? "",
        reasonId: user.reason ?? "",
        subjectPriorityId: priority && knownPriority ? priority : "",
        qualityIds: profile.qualities ?? [],
        appreciatedSubjectIds: subjectIds(user.appreciated_school_subjects),
        futureJob: profile.future_job ?? "",
        futureOrientation: profile.future_orientation ?? "",
        workFrequencyId: profile.personal_work?.frequency ?? "",
        workDurationId: profile.personal_work?.duration ?? "",
    }
}

/**
 * « En quoi Masteur peut m'être utile ? » — la liste `reasons` de `auth/RegisterScreen.js`, que les
 * ports ne peuvent pas importer (l'écran d'inscription et ses images). Recopiée mot pour mot.
 */
const REASONS = [
    "Me mettre au travail",
    "Être plus concentré dans mon travail",
    "Améliorer ma méthode de travail",
    "M'aider à m'organiser",
    "Valoriser mon travail",
    "Augmenter mes notes",
    "Prendre de l'avance",
    "Me préparer aux études supérieures",
    "Autre",
]

const sameLabel = (value: string): UISelectOption => ({ id: value, label: value })
const fromValueLabel = (option: { value: string; label: string }): UISelectOption => ({
    id: option.value,
    label: option.label,
})

/** Les sports du formulaire de profil : la page Profil et la relance « Personnalise ton profil ». */
export const SPORT_OPTIONS: UISelectOption[] = (sportOptions as string[]).map(sameLabel)

export function toProfileCatalogs(
    schoolSubjects: ApiSubject[] | null | undefined,
    personalWorkForm: ApiPersonalWorkForm | null | undefined
): UIStudentProfileCatalogs {
    return {
        classes: (studentClasses as string[]).map(sameLabel),
        sports: SPORT_OPTIONS,
        subjects: (schoolSubjects ?? [])
            .filter((subject) => subject?.id != null)
            .map((subject) => ({ id: String(subject.id), label: subject.name ?? "" })),
        reasons: REASONS.map(sameLabel),
        subjectPriorities: (subjectPriorities as Array<{ value: string; label: string }>).map(fromValueLabel),
        qualities: (qualityOptions as Array<{ value: string; label: string }>).map(fromValueLabel),
        workFrequencies: (personalWorkForm?.frequency?.values ?? []).map(sameLabel),
        workDurations: (personalWorkForm?.duration?.values ?? []).map(sameLabel),
    }
}

export const COUNTRY_OPTIONS: UISelectOption[] = (countryOptions as Array<{ value: string; label: string }>).map(
    fromValueLabel
)

// ── Notifications ──────────────────────────────────────────────────────────────────────────────

/**
 * Les types que l'élève règle — `SettingsConfigurationScreen` : ceux de ses rôles, d'abord ceux à
 * heure de rappel (`config.custom_time === true`), puis les autres (`=== false`) ; un type sans
 * `custom_time` n'était dans aucune des deux cartes, il n'est pas ici non plus. Un canal n'est
 * réglable que si le type l'offre (`config.channels.push/email === true`).
 */
export function toNotificationSettings(
    types: ApiNotificationType[] | null | undefined,
    roles: string[] | null | undefined,
    settings: ApiNotificationSettings | null | undefined
): UINotificationSetting[] {
    const forRoles = (types ?? []).filter((type) => (type?.roles ?? []).some((role) => roles?.includes(role)))
    const ordered = [
        ...forRoles.filter((type) => type.config?.custom_time === true),
        ...forRoles.filter((type) => type.config?.custom_time === false),
    ]
    return ordered
        .filter((type): type is ApiNotificationType & { slug: string } => typeof type.slug === "string")
        .map((type) => {
            const channels = settings?.[type.slug]?.channels
            const setting: UINotificationSetting = { slug: type.slug, name: type.name ?? "" }
            if (type.config?.channels?.push === true) setting.push = !!channels?.push
            if (type.config?.channels?.email === true) setting.email = !!channels?.email
            return setting
        })
}

/**
 * Une bascule — l'`onChange` de `NotificationSettingsItem` : le canal touché prend sa valeur,
 * l'autre et l'heure de rappel restent ce qu'ils étaient.
 */
export function toggleNotificationChannel(
    settings: ApiNotificationSettings | null | undefined,
    slug: string,
    channel: "push" | "email",
    next: boolean
): ApiNotificationSettings {
    const current = settings?.[slug]
    return {
        ...(settings ?? {}),
        [slug]: {
            channels: {
                push: channel === "push" ? next : current?.channels?.push,
                email: channel === "email" ? next : current?.channels?.email,
            },
            custom_time: current?.custom_time,
        },
    }
}

// ── La ligne d'abonnement de « Général » ───────────────────────────────────────────────────────

/**
 * `SubscriptionStateCard` : pour un élève qui lit ses réussites (`my_successes`), le nom de l'offre
 * (`subscription_state.plan`, « basic » sans offre, comme la puce d'aujourd'hui), « Stage » sous
 * `holiday_course_views`, et la fin de l'essai tant qu'elle est à venir. `now` : l'heure de l'appelant.
 */
export function toSubscriptionSummary(
    user: ApiStudentSettingsUser,
    now: moment.Moment
): UIStudentSubscriptionSummary | null {
    if (!hasRole(user, "student") || !canReadSubject(user?.roles, "my_successes")) return null
    const trialEnd = user.trial_ends_at ? moment(user.trial_ends_at) : null
    return {
        planLabel: user.subscription_state?.plan ?? "basic",
        holidayCourse: canReadSubject(user?.roles, "holiday_course_views"),
        trialEndsOn:
            trialEnd != null && trialEnd.isAfter(now) ? formatDayMonthYear(user.trial_ends_at as string) : null,
    }
}

/** La ligne n'ouvre « Mes privilèges » que pour un abonnement en cours ou en pause. */
export function canOpenSubscription(user: ApiStudentSettingsUser): boolean {
    const status = user.subscription_state?.status ?? ""
    return SUBSCRIBED_STATUSES.includes(status) || SUBSCRIPTION_PAUSED_STATUSES.includes(status)
}

// ── Validation (le schéma `yup` de l'ancien formulaire, page par page) ─────────────────────────

export const REQUIRED_MESSAGE = "Champ obligatoire"

export const isEmail = (value: string) => yup.string().email().isValidSync(value) as boolean

/**
 * « Général » : prénom et nom obligatoires, le téléphone au format français, l'email d'un parent
 * bien formé. Écart voulu : l'ancien schéma exigeait aussi le téléphone d'un parent, un champ que
 * l'élève ne peut pas modifier — sans lui, plus rien ne s'enregistrait. Le serveur l'accepte vide.
 */
export function validateGeneralSettings(
    values: UIStudentGeneralSettings
): Partial<Record<keyof UIStudentGeneralSettings, string>> {
    const errors: Partial<Record<keyof UIStudentGeneralSettings, string>> = {}
    if (!values.firstname.trim()) errors.firstname = REQUIRED_MESSAGE
    if (!values.lastname.trim()) errors.lastname = REQUIRED_MESSAGE
    if (values.mobile !== "" && !phoneRegExp.test(values.mobile)) {
        errors.mobile = "Le numéro de téléphone est invalide"
    }
    if (values.parentEmail !== "" && !isEmail(values.parentEmail)) errors.parentEmail = "Email invalide"
    return errors
}

/** « Adresse » : le code postal selon le pays, quand la bibliothèque connaît ce pays. */
export function validateAddressSettings(
    values: UIStudentAddressSettings
): Partial<Record<keyof UIStudentAddressSettings, string>> {
    const code = values.countryId
    if (
        values.postalCode &&
        code &&
        postcodeValidatorExistsForCountry(code) &&
        !postcodeValidator(values.postalCode, code)
    ) {
        return { postalCode: "Le code postal est invalide" }
    }
    return {}
}

// ── Le corps envoyé ────────────────────────────────────────────────────────────────────────────

export type StudentAccountEdits = {
    general?: UIStudentGeneralSettings
    address?: UIStudentAddressSettings
    profile?: UIStudentProfileSettings
    notificationSettings?: ApiNotificationSettings | null
}

/** Un choix vide part absent, comme l'option vide de l'ancien formulaire. */
const choice = (value: string): string | undefined => (value !== "" ? value : undefined)

/**
 * Le corps de `updateStudentAccount` — `dataStudent` de `SettingsTabNavigator.onSubmit` : toutes les
 * pages, celle qu'on enregistre avec ses valeurs, les autres avec celles du compte. Les matières
 * prioritaires (que l'élève ne modifie pas) repartent telles quelles.
 *
 * Le serveur ne touche pas à un champ absent du corps. Un choix vidé sur la page Profil part donc
 * `null`, pour être vidé ; enregistrée d'une autre page, il part absent : un choix que la page Profil
 * ne sait pas montrer (une priorité d'un ancien libellé) reste ce qu'il est.
 *
 * La classe ne part qu'avec la page Profil, la seule qui la montre, avec `affirm_class` : envoyée
 * par l'élève lui-même, elle AFFIRME sa classe (`UserValidators::updateAccountProfileStudent` →
 * `StudentProfile::affirmClass()`), et une page qui ne la montre pas ne peut pas l'affirmer à sa
 * place (Wajih, 18/10). Sans elle, le serveur garde la classe du compte et n'affirme rien. Vide sur
 * Profil, elle part `null` : le serveur la refuse sous son champ, comme l'ancien formulaire.
 */
export function toStudentAccountPayload(user: ApiStudentSettingsUser, edits: StudentAccountEdits = {}) {
    const profileChoice = (value: string) => choice(value) ?? (edits.profile ? null : undefined)
    // La clé est absente du corps hors Profil : présente même vide, le serveur la lirait comme dite.
    const classOfProfile = edits.profile ? { class: profileChoice(edits.profile.classId), affirm_class: true } : {}
    const general = edits.general ?? toGeneralSettings(user)
    const address = edits.address ?? toAddressSettings(user)
    const profile = edits.profile ?? toProfileSettings(user)
    const notificationSettings =
        edits.notificationSettings !== undefined ? edits.notificationSettings : (user.notification_settings ?? null)

    return {
        firstname: general.firstname,
        lastname: general.lastname,
        email: general.email,
        mobile: general.mobile,
        parent_firstname: general.parentFirstname,
        parent_lastname: general.parentLastname,
        parent_email: general.parentEmail,
        parent_mobile: general.parentMobile,
        school_name: profile.schoolName,
        city: address.city,
        street: address.address,
        complement: address.addressComplement,
        postal_code: address.postalCode,
        country: choice(address.countryId) ?? "FR",
        ...classOfProfile,
        reason: profileChoice(profile.reasonId),
        subject_priority: profileChoice(profile.subjectPriorityId),
        future_orientation: profile.futureOrientation,
        future_job: profile.futureJob,
        personal_work_frequency: profileChoice(profile.workFrequencyId),
        personal_work_duration: profileChoice(profile.workDurationId),
        qualities: profile.qualityIds,
        specialty_school_subjects: profile.specialtySubjectIds,
        prioritized_school_subjects: subjectIds(user.prioritized_school_subjects),
        appreciated_school_subjects: profile.appreciatedSubjectIds,
        notification_settings: notificationSettings,
        interests: profileChoice(profile.sportId),
    }
}

/**
 * Le corps de la relance « Personnalise ton profil » : le sport choisi, et le reste du compte tel
 * qu'il est. Sans la classe, que la relance ne montre pas : elle ne l'affirme pas à la place de
 * l'élève (Wajih, 18/10). L'ancienne modale (`ProfileCustomizationPromptModal.js`) renvoyait la classe
 * du dossier ; le serveur ne l'affirme plus sans `affirm_class`, et ne vide plus le reste du profil.
 */
export function toSportChoicePayload(user: ApiStudentSettingsUser, sport: string) {
    return { ...toStudentAccountPayload(user), interests: sport }
}

/**
 * Le corps entier se revalide : une valeur enregistrée qui ne passe plus (un téléphone, un contact
 * de parent) refuse le sport. La relance ne montre que lui ; le refus d'un autre champ ne s'y lit
 * pas tel quel.
 */
export function refusesOtherThanSport(error: unknown): boolean {
    const { fields, elsewhere } = settingsFieldErrors(error, "profile")
    return elsewhere || Object.keys(fields).some((field) => field !== "sportId")
}

// ── Les erreurs du serveur ─────────────────────────────────────────────────────────────────────

export type StudentSettingsSection = "general" | "address" | "profile" | "preferences"

/** Le champ du corps → la page et le champ qui le montrent. */
const FIELD_BY_API_KEY: Record<string, [StudentSettingsSection, string]> = {
    firstname: ["general", "firstname"],
    lastname: ["general", "lastname"],
    email: ["general", "email"],
    mobile: ["general", "mobile"],
    parent_firstname: ["general", "parentFirstname"],
    parent_lastname: ["general", "parentLastname"],
    parent_email: ["general", "parentEmail"],
    parent_mobile: ["general", "parentMobile"],
    street: ["address", "address"],
    complement: ["address", "addressComplement"],
    postal_code: ["address", "postalCode"],
    city: ["address", "city"],
    country: ["address", "countryId"],
    class: ["profile", "classId"],
    interests: ["profile", "sportId"],
    specialty_school_subjects: ["profile", "specialtySubjectIds"],
    school_name: ["profile", "schoolName"],
    reason: ["profile", "reasonId"],
    subject_priority: ["profile", "subjectPriorityId"],
    qualities: ["profile", "qualityIds"],
    appreciated_school_subjects: ["profile", "appreciatedSubjectIds"],
    future_job: ["profile", "futureJob"],
    future_orientation: ["profile", "futureOrientation"],
    personal_work_frequency: ["profile", "workFrequencyId"],
    personal_work_duration: ["profile", "workDurationId"],
}

/**
 * Les erreurs de champ d'un refus (422 : `{ message, errors: { champ: [phrases] } }`, rangé tel quel
 * dans la tranche) : celles de la page enregistrée, sous leur champ. `elsewhere` : un champ d'une
 * autre page a été refusé — sa page ne s'affiche pas, l'erreur générale le dit.
 */
export function settingsFieldErrors(
    error: unknown,
    section: StudentSettingsSection
): { fields: Record<string, string>; elsewhere: boolean } {
    const fields: Record<string, string> = {}
    let elsewhere = false
    for (const [key, message] of Object.entries(laravelFieldErrors(error))) {
        const target = FIELD_BY_API_KEY[key.split(".")[0]]
        if (target && target[0] === section) {
            if (!fields[target[1]]) fields[target[1]] = message
        } else {
            elsewhere = true
        }
    }
    return { fields, elsewhere }
}
